<template>
	<ul class="webview-notice">
		<!-- ローディング -->
		<transition name="fade">
			<webview-redraw-progress v-if="isLoading" class="webview-notice__loading" />
		</transition>
		<!-- /ローディング -->

		<sorry-page v-if="isError" @reload="getData" />

		<li
			v-else
			v-for="(notice, index) in notices"
			:key="`notice_${index}`"
			:class="`notice is-template-${notice.template}`"
			:id="`${index === notices.length - 1 ? 'js_infinite_scroll' : ''}`"
		>
			<a
				:href="notice.link !== '' ? `native://openNoticeLink?type=${notice.bannerType}&url=${notice.link}` : 'javascript:void(0);'"
				class="notice__link gtm-link_click_count01"
				:data-gtm-event-cat-label="`お知らせNo.${notice.no}クリック`"
				:data-gtm-event-act-label="`notice${notice.no}_click`"
			>
				<h2 class="notice__head"><em :class="`is-category-${notice.category}`">{{ notice.categoryName }}</em><span>{{ notice.date }}</span></h2>
				<section class="notice__main">
					<h3 class="notice__main--title">{{ notice.title }}</h3>
					<p
						v-for="(image, i) in notice.images"
						:key="`notice_image_${i}`"
						class="notice__main--image"
					>
						<img :src="`${image}`" :alt="`お知らせNo.${index + 1} 画像${i + 1}`">
					</p>
					<p class="notice__main--body" v-html="notice.body"></p>
					<p v-if="notice.link !== ''" class="notice__main--text">詳しくはこちら</p>
				</section>
			</a>
		</li>
	</ul>
</template>

<script>
// Vuex
import { mapActions, mapGetters } from 'vuex'
import { SET_IS_WEBVIEW, SET_NOTICE_DATA } from '../store/modules/common/mutation-types'

// ライブラリ
import cmnConst from '@/assets/js/constant.js'
import moment from 'moment'

// Compornents
import SorryPage from '@/views/SorryPage'
import WebviewRedrawProgress from '@/components/Molecules/WebviewRedrawProgress'

const LIMIT = 15

export default {
	name: 'WebviewNotice',
	components: {
		SorryPage,
		WebviewRedrawProgress
	},
	data () {
		return {
			isLoading: false,
			isError: false,
			userId: null,
			userToken: null,
			notices: [],
			skip: 0,
			observer: null,
			isFinish: false
		}
	},
	created () {
		// OnsenUIのbody要素に付与されているstyleを打ち消す
		const style = document.body.getAttribute('style')
		document.body.setAttribute('style', `${style} position: inherit; overflow: auto;`)

		// storeにwebviewフラグを立てる
		this.SET_IS_WEBVIEW(true)

		// URLパラメータをチェック
		const state = this.$route.query.state ? this.$route.query.state.toString() : ''
		const stateArray = state.split(cmnConst.SEPARATOR)
		if (state === 'error' || state === '') {
			// 認証エラー
			console.log('state error: ', state)
			this.isError = true
		} else if (stateArray.length !== 2) {
			// 認証パラメータエラー
			console.log('state length error: ', state)
			this.isError = true
		} else {
			// URLパラメータからユーザーID、ユーザートークンを取得する
			this.userId = stateArray[0]
			this.userToken = stateArray[1]

			// APIリクエスト処理
			this.getData()
		}
	},
	mounted () {
	},
	computed: {
		// map Vuex getters
		...mapGetters('common', ['noticeData'])
	},
	methods: {
		// map Vuex actions
		...mapActions('common', [SET_IS_WEBVIEW, SET_NOTICE_DATA]),

		// データ取得処理
		getData () {
			// 状態の初期化
			this.isLoading = true
			this.isError = false

			// NOTE: お知らせ情報を取得したらマウントする
			this.SET_NOTICE_DATA({
				user_id: this.userId,
				user_token: this.userToken,
				skip: this.skip,
				limit: LIMIT
			}).then(res => {
				console.log('res: ', res)
				this.updateNotice()

				// NOTE: レスポンスがLIMIT分ある場合、スクロールイベントを登録する
				if (this.noticeData.notices.length === LIMIT) this.$nextTick(() => this.onScroll())
			}).catch(error => {
				console.log('userData error: ', error)
				this.isError = true
			}).finally(() => {
				this.isLoading = false
			})
		},

		// お知らせ情報を更新
		updateNotice () {
			const category = ['重要', 'ニュース', 'アップデート', 'アンケート', 'PR']

			// APIから取得したお知らせ情報を取得した分だけ配列に追加
			for (let i = this.skip, len = this.noticeData.notices.length + this.skip; i < len; i++) {
				const item = this.noticeData.notices[i - this.skip]

				// サニタイズを行い、改行を<br>タグに変換
				const body = item.body.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/\n/g, '<br>')

				// タイトルが34文字超える場合、「...」で省略する
				const title = item.title.length > 34 ? `${item.title.slice(0, 34)}...` : item.title

				// $setを使って配列に追加しリアクティブにする
				this.$set(this.notices, i, {
					no: item.no,
					categoryName: category[item.category - 1],
					category: item.category,
					template: item.template,
					images: item.images,
					link: item.link,
					bannerType: item.banner_type,	// TODO: 管理画面でバナータイプに
					body: body,
					date: moment.unix(item.date).format('YYYY年MM月DD日'),
					title: title
				})
			}
		},

		// IntersectionObserver設定
		onScroll () {
			const domScroll = document.getElementById('js_infinite_scroll')

			if (domScroll) {
				this.observer = new IntersectionObserver(entries => {
					const entry = entries[0]
					if (entry.isIntersecting) {
						this.scrollNoticeData()
					}
				})
				this.observer.observe(domScroll)
			}
		},

		// 表示件数の上限追加
		scrollNoticeData () {
			this.skip += LIMIT
			this.disconnectObserver()
			this.getData()
		},

		// IntersectionObserverを解除
		disconnectObserver () {
			if (this.observer) this.observer.disconnect()
		}
	},
	beforeDestroy () {
		this.disconnectObserver()
	}
}
</script>

<style lang="scss" scoped>
@import "../assets/sass/variable";

// NOTE: このページ限定の変数（デザインシステム外のため他での利用禁止）
$spacing-1: 0.062rem;
$spacing-3: 0.187rem;
$spacing-12: 0.75rem;
$spacing-26: 1.625rem;
$spacing-36: 2.25rem;

$color-notice-date: #666;
$color-notice-news: #5CA9F2;
$color-notice-important: #FC7070;
$color-notice-update: #FFB158;
$color-notice-enquete: #A28AF5;
$color-notice-border: #F0F0F0;
$color-notice-pr: #F95959;

.webview-notice {
	font-family: $lp-family-primary;
	width: 100%;
	padding: $spacing-26 $spacing-20 0;
	margin: 0;
	color: $text-primary;
	line-height: $line-height;
	list-style: none;
	box-sizing: border-box;

	// mplusのジャギー対策
	p, span, em, a, h2, h3 { transform: rotate(0.05deg); }

	&__loading { position: fixed; }
}

.notice {
	$parent: &;
	width: 100%;
	margin: 0 auto;
	border-bottom: $spacing-3 solid $color-notice-border;

	&:last-of-type { border-bottom: 0; }

	img { max-width: 100%; }

	&__link {
		display: block;
		width: 100%;
		padding: $spacing-36 0 $spacing-36;
		text-decoration: none;
		color: $text-primary;
	}

	&:first-of-type &__link { padding-top: 0; }

	// NOTE: APIから取得するtemplateパラメータによってデザインを変更する
	// デフォルトテンプレート
	&.is-template-1 {
		#{$parent}__head {
			display: flex;
			justify-content: space-between;
			align-items: center;
			margin: 0;
			font-weight: normal;

			em {
				font-family: $family-primary;
				display: block;
				width: 31.747%;
				padding: $spacing-4 0;
				font-style: normal;
				text-align: center;
				font-size: $font-size-14;
				color: $text-seconday;
				line-height: $line-height-1;
				border-radius: $spacing-12;
				background-color: $background-secondary;

				&.is-category-1 { background-color: $color-notice-important; }
				&.is-category-2 { background-color: $color-notice-news; }
				&.is-category-3 { background-color: $color-notice-update; }
				&.is-category-4 { background-color: $color-notice-enquete; }
				&.is-category-5 {
					border: 1px solid $color-notice-pr;
					color: $color-notice-pr;
					background-color: $background-primary;
				}
			}

			span {
				font-size: $font-size-12;
				color: $color-notice-date;
				line-height: $line-height-1;
			}
		}

		#{$parent}__main {
			margin: 0;

			&--title {
				font-family: $family-primary;
				margin: $spacing-12 0 0;
				font-size: $font-size-18;
				font-weight: normal;
				overflow-wrap: break-word;
			}

			&--image {
				margin: $spacing-12 0 0;
				text-align: center;

				img {
					display: block;
					width: 100%;
				}
			}

			&--body {
				margin: $spacing-12 0 0;
				font-size: $font-size-16;
				overflow-wrap: break-word;
			}

			&--text {
				font-family: $family-primary;
				position: relative;
				top: 0;
				left: 50%;
				transform: translateX(-50%);
				display: inline-block;
				margin: $spacing-12 0 0;
				font-size: $font-size-16;
				color: $primary;
				text-align: center;
				text-decoration: none;
				border-bottom: $spacing-1 solid $primary;
			}
		}
	}
}
</style>
